import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { 
  Shield, Users, FileWarning, Heart, LogOut, 
  TrendingUp, Plus, Edit, Trash2, Star, StarOff, X, Bot, IdCard
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminTestimonialsPage = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    couple_names: "",
    story: "",
    location: "",
    wedding_date: "",
    photo_url: ""
  });
  const [stats, setStats] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [adminToken]);

  const fetchData = async () => {
    try {
      const [testimonialsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/testimonials`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);
      setTestimonials(testimonialsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(
          `${API}/admin/testimonials/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        toast.success("Testimonial updated");
      } else {
        await axios.post(
          `${API}/admin/testimonials`,
          formData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        toast.success("Testimonial created");
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({ title: "", couple_names: "", story: "", location: "", wedding_date: "", photo_url: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving testimonial");
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      title: testimonial.title || "",
      couple_names: testimonial.couple_names || "",
      story: testimonial.story || "",
      location: testimonial.location || "",
      wedding_date: testimonial.wedding_date || "",
      photo_url: testimonial.photo_url || ""
    });
    setEditingId(testimonial.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Testimonial deleted");
      fetchData();
    } catch (error) {
      toast.error("Error deleting testimonial");
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await axios.post(`${API}/admin/testimonials/${id}/feature`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Featured status updated");
      fetchData();
    } catch (error) {
      toast.error("Error updating featured status");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-rose-500 font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin Panel</span>
          </div>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <TrendingUp className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/verifications" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <IdCard className="w-5 h-5" />
            ID Verification
          </Link>
          <Link to="/admin/reports" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <FileWarning className="w-5 h-5" />
            Reports
          </Link>
          <Link to="/admin/users" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Users className="w-5 h-5" />
            Users
          </Link>
          <Link to="/admin/stories" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Heart className="w-5 h-5" />
            Love Stories
          </Link>
          <Link to="/admin/testimonials" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 text-rose-400 font-medium">
            <Star className="w-5 h-5" />
            Testimonials
          </Link>
          <Link to="/admin/seed-profiles" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Bot className="w-5 h-5" />
            Seed Profiles
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2">Testimonials</h1>
            <p className="text-slate-400">Manage success stories displayed on the website</p>
          </div>
          <Button
            onClick={() => {
              setFormData({ title: "", couple_names: "", story: "", location: "", wedding_date: "", photo_url: "" });
              setEditingId(null);
              setShowModal(true);
            }}
            className="bg-rose-500 hover:bg-rose-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Testimonial
          </Button>
        </div>

        {/* Testimonials Grid */}
        {testimonials.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700 text-center">
            <Heart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No testimonials yet</p>
            <p className="text-slate-500 text-sm mt-2">Add your first success story to display on the website</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-medium text-lg">{t.title}</h3>
                    <p className="text-rose-400">{t.couple_names}</p>
                    {t.location && <p className="text-slate-500 text-sm">{t.location}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleFeatured(t.id)}
                      className={`p-2 rounded-lg transition-colors ${t.is_featured ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400 hover:text-amber-400'}`}
                      title={t.is_featured ? "Unfeature" : "Feature"}
                    >
                      {t.is_featured ? <Star className="w-5 h-5 fill-current" /> : <StarOff className="w-5 h-5" />}
                    </button>
                    <button onClick={() => handleEdit(t)} className="p-2 bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors">
                      <Edit className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(t.id)} className="p-2 bg-slate-700 text-slate-400 hover:text-red-400 rounded-lg transition-colors">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <p className="text-slate-300 text-sm line-clamp-3">{t.story}</p>
                {t.wedding_date && (
                  <p className="text-slate-500 text-xs mt-3">Wedding: {t.wedding_date}</p>
                )}
                {t.is_featured && (
                  <span className="inline-flex items-center gap-1 mt-3 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                    <Star className="w-3 h-3 fill-current" /> Featured
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-white">
                  {editingId ? "Edit Testimonial" : "New Testimonial"}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., A Match Made in Heaven"
                  className="bg-slate-900 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Couple Names</Label>
                <Input
                  value={formData.couple_names}
                  onChange={(e) => setFormData({ ...formData, couple_names: e.target.value })}
                  placeholder="e.g., Ahmed & Fatima"
                  className="bg-slate-900 border-slate-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Story</Label>
                <Textarea
                  value={formData.story}
                  onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                  placeholder="Their love story..."
                  className="bg-slate-900 border-slate-700 text-white min-h-[120px]"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., London, UK"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Wedding Date</Label>
                  <Input
                    value={formData.wedding_date}
                    onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                    placeholder="e.g., June 2025"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Photo URL (optional)</Label>
                <Input
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-400">
                  Cancel
                </Button>
                <Button type="submit" className="bg-rose-500 hover:bg-rose-600 text-white">
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonialsPage;
